import { useEffect, type ReactNode } from "react";

// 어드민 팝업 — 오버레이 클릭과 Esc 로 닫힌다 (기존 3개 팝업이 각자 구현하던 걸 하나로 모았다).
// 닫기는 우측 상단 X 하나만 두고, 하단에는 실행 액션만 남긴다.
// 제목은 title(무엇을) + subject(누구에게) 로 나눠 받는다 — 한 줄에 구분자를 넣지 않기 위해서다.
export default function Modal({
  title,
  subject,
  description,
  width = "480px",
  height,
  onClose,
  footer,
  children,
}: {
  title: string;
  subject?: ReactNode;
  description?: ReactNode;
  width?: string;
  // 주지 않으면 내용 높이만큼 (화면을 넘으면 본문 스크롤)
  height?: string;
  onClose: () => void;
  footer?: ReactNode;
  children?: ReactNode;
}) {
  // Esc 로 닫기 — 팝업이 열려 있는 동안만 리스너를 붙인다
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    // 바깥(오버레이) 클릭 시 닫힘 — 내부 클릭은 버블링을 막아 유지한다
    <div
      className="fixed inset-0 z-20 flex items-center justify-center bg-slate-900/40 px-5 py-8"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-full w-full flex-col overflow-hidden rounded-lg bg-white shadow-xl shadow-slate-900/10"
        style={{ maxWidth: width, height }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">{title}</p>
            {subject && (
              <p className="mt-0.5 truncate text-sm text-slate-500">
                {subject}
              </p>
            )}
          </div>
          <button
            type="button"
            className="-mr-1 -mt-1 shrink-0 rounded-md px-2 py-1 text-lg leading-none text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="닫기"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* 본문만 스크롤한다 — 헤더와 액션 바는 항상 보인다 */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {description && (
            <p className="text-sm leading-relaxed text-slate-500">
              {description}
            </p>
          )}
          {children}
        </div>

        {/* 실행 액션(저장·업로드·설정 등 확인 계열)은 오른쪽이 기본 위치.
            파괴 계열은 호출처에서 mr-auto 를 붙여 왼쪽으로 뗀다 */}
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
